import { TranslationKey } from '@Localization';
import { inject, injectable, interfaces } from 'inversify';
import { isNull } from 'lodash';
import { action, makeObservable } from 'mobx';
import { downloadFileWithCheck } from '../../__legacy__/helpers/fileDownload.helpers';
import { parseParameters } from '../../__legacy__/helpers/parameters.helpers';
import { ACDLError, CUSTOM_ERROR_CODES, ForbiddenError, InputStatus } from '../../__legacy__/models/error.model';
import {
  GetAnnotationTypesRequest,
  IGetAnnotationTypesService,
} from '../../__legacy__/modules/annotation/submodules/annotationTypes/services/getAnnotationTypesService';
import { NotificationLevel, ToastNotification } from '../../__legacy__/modules/notifications/models/notification.model';
import { INotificationsService } from '../../__legacy__/modules/notifications/services/notifications.service';
import { IQuestionsApiService } from '../../__legacy__/modules/projectDetails/sub/tools/services/questionsApi.service';
import { WorkspaceRole } from '../../__legacy__/modules/workspaces/workspaces.store';
import { browserHistory } from '../../usehistory';
import { ICloudCostsApiService } from '../cloudCosts/services/cloudCostsApi.service';
import { IOverlayLoaderStore } from '../common/OverlayLoader.store';
import { ICurrentWorkspaceStore } from '../workspaces/currentWorkspace/CurrentWorkspace.store';
import { IJobDetailsStore, JOB_DETAILS_STORE_INITIAL_STATE } from './jobDetails.store';
import { IDatasetImagesDto, IInferenceDetailsDto, IJobMetricsDto, INTEGER_RESULT_NAMES, IProjectImagesDto, ITrainingDetailsDto } from './jobs.api.model';
import { IJobDownload, INFERENCE_TYPE, JOB_ANNOTATION_TYPE, JOB_DOWNLOAD_TYPE, JOB_RESOURCE_TYPE, JOB_STATUS, JOB_TYPE, TRAINING_TYPE } from './jobs.model';
import { IJobThresholdStore } from './jobThreshold.store';
import { IImagesApiService } from './services/imagesApi.service';
import { IJobActionsApiService } from './services/jobActionsApi.service';
import { IJobDetailsApiService, JOB_DETAILS_CUSTOM_ERRORS } from './services/jobDetailsApi.service';
import { IJobSettingsApiService } from './services/jobSettingsApi.service';
import { assertNotNull, AsyncGuardError, buildPredictionImage, ensureNotStale, getJobRoutePrefix, getJobTranslationPrefix } from './utils';

export interface IJobDetailsService {
  store: IJobDetailsStore;
  thresholdStore: IJobThresholdStore;

  // Overview
  populateJobDetailsAsync(jobId: string): Promise<void>;
  populateJobSettingsAsync: () => Promise<void>;
  populateJobSettingsWithDefaultsAsync: () => Promise<void>;
  populateJobStatusAsync: (jobId: string) => Promise<void>;
  populateSubTrainingJobsAsync: (jobId: string) => Promise<void>;
  populateProjectsAsync: () => Promise<void>;
  populateDatasetsAsync: () => Promise<void>;
  populateTrainings: () => Promise<void>;
  populateWorkspaceDatasetsAsync: () => Promise<void | ACDLError>;
  populateImagesAsync: () => Promise<void>;
  populatePredictionsAsync: () => Promise<void>;
  populateCalculatedCostAsync: () => Promise<void>;
  populateQuestionsAsync: () => Promise<void>;
  estimateCostAsync: () => Promise<boolean>;
  getGlobalErrors: () => Map<string, InputStatus>;
  changePaginationAsync: (pageNumber: number, pageSize: number) => Promise<void>;
  togglePredictionsAsync: () => Promise<void>;
  updateThresholdAsync: (min: number, max: number) => Promise<void>;
  downloadJobAsync: (downloadType: JOB_DOWNLOAD_TYPE) => Promise<IJobDownload | null>;
  startJobAsync: () => Promise<void>;
  stopJobAsync: () => Promise<void>;
  recalculateMetricsAsync: (min: number, max: number) => Promise<void>;
}

export namespace IJobDetailsService {
  export const $: interfaces.ServiceIdentifier<IJobDetailsService> = Symbol('JOB_DETAILS_SERVICE');
}

@injectable()
export class JobDetailsService implements IJobDetailsService {
  constructor(
    @inject(IJobDetailsStore.$) public readonly store: IJobDetailsStore,
    @inject(IJobThresholdStore.$) public readonly thresholdStore: IJobThresholdStore,
    @inject(INotificationsService.$) private readonly notificationsService: INotificationsService,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore,
    @inject(IOverlayLoaderStore.$) private readonly overlayLoaderStore: IOverlayLoaderStore,
    @inject(IGetAnnotationTypesService.$) private readonly getAnnotationTypesService: IGetAnnotationTypesService,
    @inject(ICloudCostsApiService.$) private readonly cloudCostsApiService: ICloudCostsApiService,
    @inject(IQuestionsApiService.$) private readonly questionsApiService: IQuestionsApiService,
    @inject(IJobDetailsApiService.$) private readonly jobDetailsApiService: IJobDetailsApiService,
    @inject(IJobSettingsApiService.$) private readonly jobSettingsApiService: IJobSettingsApiService,
    @inject(IImagesApiService.$) private readonly imagesApiService: IImagesApiService,
    @inject(IJobActionsApiService.$) private readonly jobActionsApiService: IJobActionsApiService
  ) {
    makeObservable(this);
  }

  get hasHeaderErrors(): boolean {
    const { validationErrors } = this.store;
    return validationErrors.header.size > 0 && Array.from(validationErrors.header.values()).some((error) => error !== null);
  }

  @action
  addCustomError = (errorCode: string): void => {
    const { validationErrors } = this.store;
    validationErrors.showAll = true;

    const code = errorCode as CUSTOM_ERROR_CODES;

    if (code === CUSTOM_ERROR_CODES.COST_LIMIT_EXCEEDED) {
      const isOwner = this.currentWorkspaceStore.isInRole([WorkspaceRole.Owner]);

      validationErrors.custom.set(code.toLocaleLowerCase(), {
        message: `errors.${isOwner ? 'OWNER_' : ''}${code}` as TranslationKey,
      });
    } else if (code === CUSTOM_ERROR_CODES.WORKSPACE_SPACE_LIMIT_REACHED) {
      if (this.store.jobType === JOB_TYPE.TRAINING) {
        validationErrors.custom.set(code.toLocaleLowerCase(), {
          message: 'errors.TRAINING_WORKSPACE_SPACE_LIMIT_REACHED',
        });
      } else {
        validationErrors.custom.set(code.toLocaleLowerCase(), {
          message: 'errors.EVALUATION_WORKSPACE_SPACE_LIMIT_REACHED',
        });
      }
    } else {
      validationErrors.custom.set(code.toLocaleLowerCase(), {
        message: `errors.${code}` as TranslationKey,
      });
    }

    this.store.validationErrors = { ...validationErrors };
  };

  notifyAboutApiSuccess = async (code: string): Promise<void> => {
    await this.notificationsService.push(
      new ToastNotification(NotificationLevel.SUCCESS, `${getJobTranslationPrefix(this.store.jobType)}.${code}` as TranslationKey)
    );
  };

  notifyAboutApiError = async (result: unknown, errorCode: string, dontShowBadRequest: boolean = false): Promise<void> => {
    if (
      result instanceof ACDLError &&
      !(result instanceof ForbiddenError) &&
      !(result instanceof AsyncGuardError) &&
      (!dontShowBadRequest || !result.isBadRequest())
    ) {
      await this.notificationsService.push(
        new ToastNotification(NotificationLevel.ERROR, `${getJobTranslationPrefix(this.store.jobType)}.${errorCode}` as TranslationKey)
      );
    }
  };

  wrapEnsureNotStale = async <T>(method: () => Promise<T>): Promise<T | ACDLError> => {
    return ensureNotStale(method, () => this.store.storeVersion);
  };

  enableAllLoaders = (): void => {
    this.overlayLoaderStore.enableLoader('job-header');
    this.overlayLoaderStore.enableLoader('job-datasets-table');
    this.overlayLoaderStore.enableLoader('job-annotation-tools-table');
    this.overlayLoaderStore.enableLoader('job-images-loader');
    this.overlayLoaderStore.enableLoader('job-settings-list');
    this.overlayLoaderStore.enableLoader('job-training-jobs-table');
  };

  disableAllLoaders = (): void => {
    this.overlayLoaderStore.disableLoader('job-header');
    this.overlayLoaderStore.disableLoader('job-datasets-table');
    this.overlayLoaderStore.disableLoader('job-annotation-tools-table');
    this.overlayLoaderStore.disableLoader('job-images-loader');
    this.overlayLoaderStore.disableLoader('job-settings-list');
    this.overlayLoaderStore.disableLoader('job-training-jobs-table');
  };

  @action
  populateJobDetailsAsync = async (jobId: string): Promise<void> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const jobType = this.store.jobType;
    const workspaceId = currentWorkspace.id;
    this.enableAllLoaders();
    const jobDetails = await this.wrapEnsureNotStale(async () => this.jobDetailsApiService.getJobDetailsAsync(workspaceId, jobId, jobType));

    if (jobDetails instanceof AsyncGuardError) {
      return;
    }

    if (!this.assertJobExists(jobDetails)) {
      return;
    }

    let trainingDetails = null;
    if (jobType === JOB_TYPE.INFERENCE) {
      const { training_job_id } = jobDetails as IInferenceDetailsDto;
      trainingDetails = await this.wrapEnsureNotStale(async () =>
        this.jobDetailsApiService.getJobDetailsAsync(workspaceId, training_job_id, JOB_TYPE.TRAINING)
      );

      if (trainingDetails instanceof AsyncGuardError) {
        return;
      }

      if (!this.assertJobExists(trainingDetails)) {
        return;
      }
    }

    this.thresholdStore.setThreshold(jobId, jobDetails.threshold);
    const jobObject: Partial<Record<keyof IJobDetailsStore['job'], unknown>> = {
      id: jobDetails.id,
      username: jobDetails.username,
      datasetIds: jobDetails.config.datasets.datasets_ids,
      projectId: jobDetails.config.datasets.project_id,
      dateCreated: jobDetails.config.date_created,
      isOwner: jobDetails.config.is_owner,
      status: jobDetails.config.status === JOB_STATUS.FAILING ? JOB_STATUS.STOPPING : jobDetails.config.status,
      startingAdditionalInfo: [],
      annotationToolIds: [],
      // Forward these values as they might be already present from /status call
      // This avoid flickering of N/A values and missed properties
      calculatedCost: this.store.job.calculatedCost,
      trainingTime: this.store.job.trainingTime,
      failureReason: this.store.job.failureReason,
    };

    if (jobType === JOB_TYPE.INFERENCE) {
      const { config, model_variant, training_job_id } = jobDetails as IInferenceDetailsDto;

      jobObject.name = config.inference_name;
      jobObject.description = config.inference_description || '';
      jobObject.variant = model_variant;
      jobObject.trainingId = training_job_id;
      jobObject.isTrainingOwner = config.is_training_owner;
      jobObject.config = { ...config.inference_config_ };
      jobObject.trainingProjectId = trainingDetails?.config.datasets.project_id;
      jobObject.type = config.datasets.project_id ? INFERENCE_TYPE.PROJECT : INFERENCE_TYPE.DATASET;
    }

    if (jobType === JOB_TYPE.TRAINING) {
      const { config, is_auto_training, parent_job_id, annotation_tools } = jobDetails as ITrainingDetailsDto;
      jobObject.isOwner = config.is_owner;
      jobObject.name = config.model_name;
      jobObject.description = config.model_description;
      jobObject.variant = config.model_variant;
      jobObject.config = { ...config.model_config_ };
      jobObject.annotationToolIds = annotation_tools;
      jobObject.type = is_auto_training ? TRAINING_TYPE.AUTO : TRAINING_TYPE.MANUAL;
      jobObject.parentTrainingId = parent_job_id;
    }

    this.store.job = jobObject as IJobDetailsStore['job'];
    const { job } = this.store;

    assertNotNull(job.id, 'Invalid job initialization');

    const canReadCosts = this.currentWorkspaceStore.isInRole(
      job.isOwner ? [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Trainer] : [WorkspaceRole.Owner, WorkspaceRole.Manager]
    );
    const promises = [
      canReadCosts ? this.populateCalculatedCostAsync() : Promise.resolve(),
      this.populateJobSettingsAsync(),
      this.populateProjectsAsync(),
      this.populateImagesAsync(),
      this.populateQuestionsAsync(true),
      this.populateAnnotationTypes(),
    ];

    if (jobType === JOB_TYPE.INFERENCE) {
      promises.push(job.type === INFERENCE_TYPE.DATASET ? this.populateWorkspaceDatasetsAsync(true) : this.populateDatasetsAsync());

      // Model can't be edited in this stage so we can get the data from training's details
      if (jobObject.status === JOB_STATUS.FINISHED && trainingDetails) {
        this.store.dataSources.trainings = [
          {
            value: trainingDetails.id,
            label: trainingDetails.config.model_name,
          },
        ];
      } else {
        promises.push(this.populateTrainings());
      }
    }

    if (jobType === JOB_TYPE.TRAINING) {
      promises.push(this.populateDatasetsAsync(), job.type === TRAINING_TYPE.AUTO ? this.populateSubTrainingJobsAsync(job.id) : Promise.resolve());
    }

    await Promise.allSettled(promises);
    this.disableAllLoaders();
  };

  @action
  populateAnnotationTypes = async (): Promise<void> => {
    const { jobType, job } = this.store;
    const projectId = jobType === JOB_TYPE.TRAINING ? job.projectId : job.trainingProjectId;

    assertNotNull(projectId, 'Tried to populate annotation types without any project');

    const request = new GetAnnotationTypesRequest(projectId);
    const annotationsResult = await this.wrapEnsureNotStale(async () => this.getAnnotationTypesService.requestAnnotationTypesAsync(request));
    if (annotationsResult instanceof ACDLError) {
      await this.notifyAboutApiError(annotationsResult, 'fetching.failed.get_predictions');
      return;
    }

    this.store.annotationTypes = annotationsResult.annotationTypes;
  };

  _populateJobSettingsAsync = async (): Promise<ReturnType<typeof parseParameters> | null> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const {
      job: { variant },
    } = this.store;

    assertNotNull(variant, 'Tried to populate settings without variant selected');
    this.overlayLoaderStore.enableLoader('job-settings-list');
    const result = await this.wrapEnsureNotStale(async () => this.jobSettingsApiService.getJobSettingsAsync(currentWorkspace.id, variant, this.store.jobType));
    if (result instanceof ACDLError) {
      return null;
    }

    this.overlayLoaderStore.disableLoader('job-settings-list');
    return parseParameters(result.params);
  };

  @action
  populateJobSettingsAsync = async (): Promise<void> => {
    const parsedSettings = await this._populateJobSettingsAsync();

    if (!parsedSettings) {
      return;
    }

    this.store.params = parsedSettings.parsedParameters;
  };

  @action
  populateJobSettingsWithDefaultsAsync = async (): Promise<void> => {
    const parsedSettings = await this._populateJobSettingsAsync();

    if (!parsedSettings) {
      return;
    }

    this.store.params = parsedSettings.parsedParameters;
    this.store.job.config = parsedSettings.preselectedValues;
  };

  @action
  populateJobStatusAsync = async (jobId: string): Promise<void> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const jobStatus = await this.wrapEnsureNotStale(async () => this.jobDetailsApiService.getJobStatusAsync(currentWorkspace.id, jobId, this.store.jobType));
    if (jobStatus instanceof ACDLError) {
      await this.notifyAboutApiError(jobStatus, 'fetching.failed.get_status');
      return;
    }

    const { status, starting_progress, cost, job_progress, additional_info, training_time, failure_reason } = jobStatus;
    this.store.job.status = status === JOB_STATUS.FAILING ? JOB_STATUS.STOPPING : status;
    this.store.job.startingProgress = starting_progress;
    this.store.job.calculatedCost = cost;
    this.store.job.startingAdditionalInfo = additional_info;
    this.store.job.trainingTime = training_time;
    this.store.job.failureReason = failure_reason;

    this.parseJobProgress(job_progress);
  };

  @action
  populateSubTrainingJobsAsync = async (jobId: string): Promise<void> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const result = await this.wrapEnsureNotStale(async () => this.jobDetailsApiService.getSubTrainingJobsAsync(currentWorkspace.id, jobId));
    if (result instanceof ACDLError) {
      await this.notifyAboutApiError(result, 'fetching.failed.get_auto_training_jobs');
      return;
    }

    this.store.trainingJobs = result.map((job) => ({
      id: job.job_id,
      name: job.model_name,
      status: job.status === JOB_STATUS.FAILING ? JOB_STATUS.STOPPING : job.status,
      progress: job.progress,
      dateCreated: job.date_created,
      trainingTime: job.training_time,
      score: job.score,
      isBest: job.is_best,
      inferenceTime: job.average_inference_time,
      params: job.params,
    }));
    this.overlayLoaderStore.disableLoader('job-training-jobs-table');
  };

  @action
  populateProjectsAsync = async (): Promise<void> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const loaderAlreadyVisible = this.overlayLoaderStore.isSpinnerVisible('job-header');
    this.overlayLoaderStore.enableLoader('job-header');
    const result = await this.wrapEnsureNotStale(async () => this.imagesApiService.getWorkspaceProjectsAsync({ workspaceId: currentWorkspace.id }));
    if (result instanceof ACDLError) {
      return;
    }

    this.store.dataSources.projects = result.map((project) => ({
      value: project.id,
      label: project.name,
      modelVariant: project.modelVariant ?? undefined,
      sizeInMB: project.sizeInMB,
    }));

    if (loaderAlreadyVisible) {
      return;
    }

    this.overlayLoaderStore.disableLoader('job-header');
  };

  @action
  populateDatasetsAsync = async (): Promise<void> => {
    const {
      job: { projectId },
    } = this.store;

    assertNotNull(projectId, 'Tried to populate datasets without project selected');
    this.overlayLoaderStore.enableLoader('job-datasets-table');
    const result = await this.wrapEnsureNotStale(async () => this.imagesApiService.getProjectDatasetsAsync({ projectId }));
    if (result instanceof ACDLError) {
      return;
    }

    this.store.dataSources.datasets = result;
    this.overlayLoaderStore.disableLoader('job-datasets-table');
  };

  @action
  populateTrainings = async (): Promise<void> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const {
      job: { variant },
    } = this.store;

    assertNotNull(variant, 'Tried to populate training without variant selected');
    const result = await this.wrapEnsureNotStale(async () => this.jobSettingsApiService.getVariantModelsAsync(currentWorkspace.id, variant));
    if (result instanceof ACDLError) {
      return;
    }

    this.store.dataSources.trainings = result.map((model) => ({
      value: model.job_id,
      label: model.model_name,
    }));
  };

  @action
  populateWorkspaceDatasetsAsync = async (assignDataset: boolean = false): Promise<void> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const loaderAlreadyVisible = this.overlayLoaderStore.isSpinnerVisible('job-header');
    this.overlayLoaderStore.enableLoader('job-header');
    const result = await this.wrapEnsureNotStale(async () => this.imagesApiService.getWorkspaceDatasetsAsync({ workspaceId: currentWorkspace.id }));
    if (result instanceof ACDLError) {
      return;
    }

    this.store.dataSources.datasetsInWorkspace = result.map((item) => ({
      id: item.id,
      name: item.name,
      description: item.description,
      imagesCount: item.imagesCount,
      createdDate: item.createdDate,
      isSelected: false,
      sizeInMB: item.sizeInMB,
    }));

    if (!loaderAlreadyVisible) {
      this.overlayLoaderStore.disableLoader('job-header');
    }

    if (!assignDataset) {
      return;
    }

    const {
      job: { datasetIds },
    } = this.store;

    const dataset = result.find((item) => item.id === datasetIds[0]);
    if (!dataset) {
      return;
    }

    this.store.dataSources.datasets = [
      {
        id: dataset.id,
        name: dataset.name,
        description: dataset.description,
        imagesCount: dataset.imagesCount,
        createdDate: dataset.createdDate,
        isSelected: false,
        sizeInMB: dataset.sizeInMB,
      },
    ];
  };

  @action
  populateImagesAsync = async (): Promise<void> => {
    const { pageNumber, pageSize, orderBy, orderType, search } = this.store.images.paging;
    const {
      job: { projectId, datasetIds },
    } = this.store;

    this.overlayLoaderStore.enableLoader('job-images-loader');
    const request: Partial<IProjectImagesDto & IDatasetImagesDto> = {
      pageNumber,
      pageSize,
      orderBy,
      orderType,
      search,
    };

    if (projectId) {
      request.projectId = projectId;
      request.datasetsIds = datasetIds;
    } else {
      request.datasetId = datasetIds[0];
    }

    const result = await this.wrapEnsureNotStale(async () =>
      projectId
        ? this.imagesApiService.getProjectImagesAsync(request as IProjectImagesDto)
        : this.imagesApiService.getDatasetImagesAsync(request as IDatasetImagesDto)
    );
    if (result instanceof ACDLError) {
      return;
    }

    if (result.pagesCount < result.pageNumber && result.pagesCount !== 0) {
      this.store.images.paging.pageNumber = result.pagesCount;
      await this.populateImagesAsync();
      return;
    }

    this.store.images.images = result.data.map((image) => ({
      ...image,
      imageSetType: image.imageSet,
      maskUrl: null,
      heatmapUrl: null,
      predictedPoints: null,
      predictedRectangles: null,
      prediction: {
        groundTruth: null,
        prediction: null,
        confusionMatrix: null,
        recall: null,
        precision: null,
        f1: null,
        auc: null,
        count: null,
        precisionRecallCurve: null,
        score: null,
        averageEntropy: null,
        confidence: null,
      },
    }));
    this.store.images.paging = {
      ...this.store.images.paging,
      orderType,
      orderBy,
      pageNumber: result.pageNumber,
      pagesCount: result.pagesCount,
      totalCount: result.totalCount,
    };

    this.overlayLoaderStore.disableLoader('job-images-loader');

    if (!this.store.images.paging.fetchPredictions) {
      return;
    }

    await this.populatePredictionsAsync();
  };

  @action
  populatePredictionsAsync = async (): Promise<void> => {
    const {
      jobType,
      job,
      images: { images },
      annotationTypes,
    } = this.store;
    const { currentWorkspace } = this.currentWorkspaceStore;

    assertNotNull(job.id, 'Tried to populate predictions without job');
    this.overlayLoaderStore.enableLoader('job-images-predictions-loader');
    const imagesIds = images.map((image) => image.id);
    const result = await this.wrapEnsureNotStale(async () => {
      assertNotNull(job.id, 'Tried to populate predictions without job');
      return this.jobDetailsApiService.getJobImagePredictionsAsync(currentWorkspace.id, job.id, imagesIds, jobType);
    });

    if (result instanceof ACDLError) {
      this.store.images.paging.fetchPredictions = false;
      await this.notifyAboutApiError(result, 'fetching.failed.get_predictions');
      return;
    }

    this.store.images.images = images
      .map((image) => {
        if (!result[image.id]) {
          return null;
        }

        const { heatmap_id, mask_id } = result[image.id];
        assertNotNull(job.id, 'Tried to populate predictions without job');
        return buildPredictionImage(
          image,
          job,
          result[image.id],
          heatmap_id ? this.jobDetailsApiService.getResourceUrl(currentWorkspace.id, job.id, jobType, heatmap_id, JOB_RESOURCE_TYPE.HEATMAP) : null,
          mask_id ? this.jobDetailsApiService.getResourceUrl(currentWorkspace.id, job.id, jobType, mask_id, JOB_RESOURCE_TYPE.MASK) : null,
          annotationTypes
        );
      })
      .filter((image) => !isNull(image));

    this.overlayLoaderStore.disableLoader('job-images-predictions-loader');
  };

  @action
  populateCalculatedCostAsync = async (): Promise<void> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const {
      job: { id },
    } = this.store;

    assertNotNull(id, 'Tried to populate calculated cost without job');
    const result = await this.wrapEnsureNotStale(async () => this.cloudCostsApiService.getJobCalculatedCostAsync(currentWorkspace.id, id, this.store.jobType));
    if (result instanceof ACDLError) {
      return;
    }

    this.store.job.calculatedCost = result.cost_calculated;
  };

  @action
  populateQuestionsAsync = async (forfeitIdsInitialization?: boolean): Promise<void> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const { projectId, variant } = this.store.job;
    this.overlayLoaderStore.enableLoader('job-annotation-tools-table');
    assertNotNull(projectId, 'Tried to populate questions without selected project');
    assertNotNull(variant, 'Tried to populate questions without selected variant');
    const result = await this.wrapEnsureNotStale(async () => this.questionsApiService.getProjectDetailsAnnotationViewAsync({ projectId }));

    if (result instanceof ACDLError) {
      await this.notifyAboutApiError(result, 'fetching.failed.get_annotation_tools');
      return;
    }

    const annotationTools = [
      ...result.annotationTypes.map((annotationType) => ({
        id: annotationType.id,
        tool: JOB_ANNOTATION_TYPE.MARKING_TOOL,
        name: annotationType.name,
        type: annotationType.selectorType,
      })),
      ...result.questions.map((question) => ({
        id: question.id,
        tool: JOB_ANNOTATION_TYPE.QUESTION,
        name: question.questionText,
        type: question.type,
      })),
    ];

    const resultVerifyTools = await this.wrapEnsureNotStale(async () =>
      this.jobSettingsApiService.postVerifyAnnotationToolsAsync(currentWorkspace.id, variant, annotationTools)
    );
    if (resultVerifyTools instanceof ACDLError) {
      await this.notifyAboutApiError(result, 'fetching.failed.get_annotation_tools');
      return;
    }

    this.store.annotationTools = resultVerifyTools;
    if (!forfeitIdsInitialization) {
      this.store.job.annotationToolIds = resultVerifyTools.filter((annotationTool) => annotationTool.compatible).map((annotationTool) => annotationTool.id);
      this.store.validationErrors = {
        ...this.store.validationErrors,
        annotationTools: this.store.job.annotationToolIds.length === 0,
      };
    }
    this.overlayLoaderStore.disableLoader('job-annotation-tools-table');
  };

  @action
  estimateCostAsync = async (): Promise<boolean> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    this.clearErrors();
    this.validateInitialEmptyFields();
    const formValidationErrors = this.getGlobalErrors();

    const {
      job: { datasetIds, config, name, description, variant, type, projectId },
      validationErrors,
      jobType,
    } = this.store;

    this.store.validationErrors = { ...validationErrors, showAll: true };

    if (!variant || formValidationErrors.size > 0 || this.hasHeaderErrors) {
      return false;
    }

    const request =
      jobType === JOB_TYPE.INFERENCE
        ? {
            inference_description: description,
            inference_config_: config,
            inference_name: name,
            datasets: {
              project_id: projectId || undefined,
              datasets_ids: datasetIds,
            },
          }
        : {
            model_description: description,
            model_config_: config,
            model_variant: variant,
            model_name: name,
            datasets: {
              project_id: this.store.job.projectId,
              datasets_ids: datasetIds,
            },
            annotation_tools: this.store.annotationTools.filter((tool) => this.store.job.annotationToolIds.includes(tool.id)),
          };
    const params = jobType === JOB_TYPE.TRAINING ? { auto_training: type === TRAINING_TYPE.AUTO } : { training_job_id: this.store.job.trainingId };
    const result = await this.wrapEnsureNotStale(async () => this.jobDetailsApiService.postJobEstimateCostAsync(currentWorkspace.id, request, jobType, params));
    if (result instanceof ACDLError) {
      if (result instanceof AsyncGuardError) {
        return false;
      }

      if (result.hasErrorCodes() && result.withCode(JOB_DETAILS_CUSTOM_ERRORS)) {
        this.clearErrors();

        result.apiErrorResponse?.errorCodes?.forEach(this.addCustomError);
      } else {
        await this.notifyAboutApiError(result, 'fetching.failed.estimate_cost');
      }
      return false;
    }

    this.store.estimatedCost = result.cost_estimated;

    this.clearErrors();
    return true;
  };

  parseJobMetrics = (metrics: IJobMetricsDto['metrics']): void => {
    this.store.metrics.results = metrics.results.map((result, idx) =>
      INTEGER_RESULT_NAMES.includes(result.name as (typeof INTEGER_RESULT_NAMES)[number])
        ? { ...result, id: idx.toString() }
        : {
            ...result,
            id: idx.toString(),
            train: result.train !== null ? Number(result.train).toFixed(3) : null,
            test: result.test !== null ? Number(result.test).toFixed(3) : null,
            validation: result.validation !== null ? Number(result.validation).toFixed(3) : null,
          }
    );

    if (metrics.confusion_matrix) {
      this.store.metrics.matrix = metrics.confusion_matrix;
    }

    this.store.metrics.metrics = {
      auc: metrics.auc?.toFixed(3) ?? null,
    };

    this.store.metrics.precisionRecall = metrics.precision_recall_curve;
  };

  parseJobProgress = (jobProgress: IJobMetricsDto): void => {
    const { loss, validationAccuracy, trainingAccuracy } = this.store.metrics;

    if (jobProgress.train_loss?.length && jobProgress.train_loss.length - loss.length > 0) {
      this.store.metrics.loss = jobProgress.train_loss;
    }

    if (jobProgress.validation_score?.length && jobProgress.validation_score.length - validationAccuracy.length > 0) {
      this.store.metrics.validationAccuracy = jobProgress.validation_score;
    }

    if (jobProgress.train_score?.length && jobProgress.train_score.length - trainingAccuracy.length > 0) {
      this.store.metrics.trainingAccuracy = jobProgress.train_score;
    }

    if (!jobProgress.metrics) {
      return;
    }

    this.parseJobMetrics(jobProgress.metrics);
  };

  @action
  validateInitialEmptyFields = (): void => {
    const {
      job: { datasetIds, projectId, variant, annotationToolIds, type, trainingId },
      images,
      validationErrors,
      jobType,
    } = this.store;

    if (datasetIds.length === 0) {
      validationErrors.datasets = true;
      validationErrors.images = true;
    } else {
      validationErrors.datasets = false;
      validationErrors.images = images.paging.pagesCount === 0;
    }

    const validateInferenceFields = (): void => {
      validationErrors.header.set(
        'project',
        type === INFERENCE_TYPE.PROJECT && !projectId
          ? {
              message: 'fields.text_validation_error',
            }
          : null
      );
      validationErrors.header.set(
        'dataset',
        type === INFERENCE_TYPE.DATASET && !datasetIds.length
          ? {
              message: 'fields.text_validation_error',
            }
          : null
      );
      validationErrors.header.set(
        'training',
        !trainingId
          ? {
              message: 'fields.text_validation_error',
            }
          : null
      );
    };

    const validateTrainingFields = (): void => {
      validationErrors.header.set(
        'project',
        projectId
          ? null
          : {
              message: 'fields.text_validation_error',
            }
      );
      validationErrors.annotationTools = annotationToolIds.length === 0;
      validationErrors.header.set(
        'variant',
        variant
          ? null
          : {
              message: 'fields.text_validation_error',
            }
      );
    };

    switch (jobType) {
      case JOB_TYPE.INFERENCE:
        validateInferenceFields();
        break;
      case JOB_TYPE.TRAINING:
        validateTrainingFields();
        break;
    }
  };

  @action
  clearErrors = (): void => {
    this.store.validationErrors = JOB_DETAILS_STORE_INITIAL_STATE.validationErrors;
  };

  @action
  getGlobalErrors = (): Map<string, InputStatus> => {
    const {
      job: { datasetIds, annotationToolIds, variant },
      validationErrors,
      jobType,
    } = this.store;
    const translationPrefix = getJobTranslationPrefix(jobType);
    const errors: Map<string, InputStatus> = new Map<string, InputStatus>();

    if (validationErrors.settings.size > 0 && Array.from(validationErrors.settings.values()).some((error) => error !== null)) {
      errors.set('settings', InputStatus.buildFrom([`${translationPrefix}.errors.validation_error`]));
    }
    if (!variant) {
      errors.set('settings', InputStatus.buildFrom([`training.errors.no_chosen_variant`]));
    }
    if (datasetIds.length === 0) {
      errors.set('datasets', InputStatus.buildFrom([`${translationPrefix}.errors.no_chosen_datasets`]));
    }
    if (jobType === JOB_TYPE.TRAINING && annotationToolIds.length === 0) {
      errors.set('annotationTools', InputStatus.buildFrom([`training.errors.no_chosen_annotation_tools`]));
    }

    validationErrors.custom.forEach((value, name) => {
      if (value === null) {
        return;
      }

      errors.set(name, InputStatus.buildFrom([value.message]));
    });

    return errors;
  };

  @action
  changePaginationAsync = async (pageNumber: number, pageSize: number): Promise<void> => {
    this.store.images.paging.pageNumber = pageNumber;
    this.store.images.paging.pageSize = pageSize;

    await this.populateImagesAsync();
  };

  @action
  togglePredictionsAsync = async (): Promise<void> => {
    this.store.images.paging.fetchPredictions = !this.store.images.paging.fetchPredictions;
    const alreadyFetched = this.store.images.images.some((image) => Object.values(image.prediction).some((v) => !isNull(v)));

    if (this.store.images.paging.fetchPredictions && !alreadyFetched) {
      await this.populatePredictionsAsync();
    }
  };

  @action
  updateThresholdAsync = async (min: number, max: number): Promise<void> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const {
      job: { id },
      jobType,
    } = this.store;

    assertNotNull(this.thresholdStore.threshold, 'Tried to update invalid threshold');
    assertNotNull(id, 'Tried to update threshold without job');
    const result = await this.wrapEnsureNotStale(async () =>
      this.jobDetailsApiService.postJobThresholdsAsync(
        currentWorkspace.id,
        id,
        {
          min_threshold: min,
          max_threshold: max,
        },
        jobType
      )
    );

    if (result instanceof ACDLError) {
      await this.notifyAboutApiError(result, 'fetching.failed.save_threshold');
      return;
    }

    // Triggers status refresh
    this.store.job.status = JOB_STATUS.SAVING;
  };

  @action
  downloadJobAsync = async (downloadType: JOB_DOWNLOAD_TYPE): Promise<IJobDownload | null> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const {
      job: { id },
      jobType,
    } = this.store;

    assertNotNull(id, 'Tried to download without job');
    const downloadUrlGenerator = async (): Promise<string | null> => {
      const downloadUrls = this.jobActionsApiService.getJobDownloadUrls(currentWorkspace.id, id, jobType);
      const token = await this.jobActionsApiService.getJobDownloadToken(currentWorkspace.id, id, jobType);
      if (token instanceof ACDLError) {
        return null;
      }

      return `${downloadUrls[downloadType]}?jwt=${token.jwt}`;
    };

    if (jobType === JOB_TYPE.INFERENCE) {
      const discrepancy = await this.wrapEnsureNotStale(async () => this.jobDetailsApiService.getJobThresholdDiscrepancy(currentWorkspace.id, id, jobType));
      if (discrepancy instanceof ACDLError) {
        return null;
      }

      if (discrepancy !== null) {
        return {
          downloadType,
          urlGenerator: downloadUrlGenerator,
          baseThresholdRange: [discrepancy.base_min_threshold, discrepancy.base_max_threshold],
          currentThresholdRange: [discrepancy.current_min_threshold, discrepancy.current_max_threshold],
        };
      }
    }

    const downloadUrl = await downloadUrlGenerator();
    if (downloadUrl) {
      await downloadFileWithCheck(downloadUrl);
    }
    return null;
  };

  @action
  startJobAsync = async (): Promise<void> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    this.clearErrors();
    this.validateInitialEmptyFields();
    const formValidationErrors = this.getGlobalErrors();

    const {
      job: { id, datasetIds, config, name, description, variant, type, projectId },
      validationErrors,
      jobType,
    } = this.store;

    this.store.validationErrors = { ...validationErrors, showAll: true };

    if (!variant || formValidationErrors.size > 0 || this.hasHeaderErrors) {
      return;
    }

    const request =
      jobType === JOB_TYPE.INFERENCE
        ? {
            inference_description: description,
            inference_config_: config,
            inference_name: name,
            datasets: {
              project_id: projectId || undefined,
              datasets_ids: datasetIds,
            },
          }
        : {
            model_description: description,
            model_config_: config,
            model_variant: variant,
            model_name: name,
            datasets: {
              project_id: this.store.job.projectId,
              datasets_ids: datasetIds,
            },
            annotation_tools: this.store.annotationTools.filter((tool) => this.store.job.annotationToolIds.includes(tool.id)),
          };
    const params = jobType === JOB_TYPE.TRAINING ? { auto_training: type === TRAINING_TYPE.AUTO } : { training_job_id: this.store.job.trainingId };
    const result = await this.wrapEnsureNotStale(async () =>
      id
        ? this.jobActionsApiService.postJobRestartAsync(currentWorkspace.id, id, jobType, request)
        : this.jobActionsApiService.postJobStartAsync(currentWorkspace.id, jobType, request, params)
    );

    if (result instanceof ACDLError) {
      if (result instanceof AsyncGuardError) {
        return;
      }

      if (result.hasErrorCodes() && result.withCode(JOB_DETAILS_CUSTOM_ERRORS)) {
        this.clearErrors();
        result.apiErrorResponse?.errorCodes?.forEach(this.addCustomError);
      } else {
        await this.notifyAboutApiError(result, `fetching.failed.${id ? 'restart' : 'start'}`);
      }

      return;
    }

    this.store.job.status = JOB_STATUS.STARTING;
    this.store.job.failureReason = null;
    browserHistory.push(getJobRoutePrefix(jobType).Details.Metrics.withParams({ workspaceId: currentWorkspace.id, jobId: result.job_id }));

    this.clearErrors();
    await this.notifyAboutApiSuccess(`fetching.success.${id ? 'restart' : 'start'}`);
  };

  @action
  stopJobAsync = async (): Promise<void> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const {
      job: { id },
      jobType,
    } = this.store;

    assertNotNull(id, 'Tried to stop without job');
    const result = await this.wrapEnsureNotStale(async () => this.jobActionsApiService.postJobStopAsync(currentWorkspace.id, id, jobType));
    if (result instanceof ACDLError) {
      await this.notifyAboutApiError(result, 'fetching.failed.stop');
      return;
    }

    await this.notifyAboutApiSuccess('fetching.success.stop');
    this.store.job.status = JOB_STATUS.STOPPING;
    this.store.job.startingAdditionalInfo = [];
  };

  @action
  recalculateMetricsAsync = async (min: number, max: number): Promise<void> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const {
      job: { id },
      jobType,
    } = this.store;

    assertNotNull(this.thresholdStore.threshold, 'Tried to recalculate metrics without threshold');
    assertNotNull(id, 'Tried to recalculate metrics without job');
    this.thresholdStore.setThresholdRange(min, max);
    this.overlayLoaderStore.enableLoader('job-recalculate-metrics-loader');
    const result = await this.wrapEnsureNotStale(async () =>
      this.jobDetailsApiService.getJobRecalculateMetricsAsync(
        currentWorkspace.id,
        id,
        {
          min_threshold: min,
          max_threshold: max,
        },
        jobType
      )
    );

    if (result instanceof ACDLError) {
      await this.notifyAboutApiError(result, 'fetching.failed.recalculate_metrics');
      return;
    }
    this.parseJobMetrics(result);
    this.overlayLoaderStore.disableLoader('job-recalculate-metrics-loader');
  };

  assertJobExists = (result: IInferenceDetailsDto | ITrainingDetailsDto | ACDLError): result is IInferenceDetailsDto | ITrainingDetailsDto => {
    if (result instanceof ACDLError) {
      if (result.hasErrorCodes() && result.withCode([CUSTOM_ERROR_CODES.EVALUATION_NOT_FOUND, CUSTOM_ERROR_CODES.TRAINING_NOT_FOUND])) {
        browserHistory.replace(getJobRoutePrefix(this.store.jobType).NotFound.Path);
      }

      return false;
    }

    return true;
  };
}
